/**
 * PanelContext
 * Tracks visibility of the three v3 chrome panels: left nav, bottom tabs, right tray.
 * Spec §1.9: state persisted in localStorage, survives navigation.
 *
 * The left nav has a tri-state cycle (expanded → collapsed → hidden → expanded)
 * per spec §1.2. PanelContext owns this state; LeftNav reads from it as a prop.
 * The left panel toggle in ScrollableTitleRow cycles this tri-state.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { LeftNavState } from '../components/navigation/LeftNav';

const LEFT_NAV_CYCLE: LeftNavState[] = ['expanded', 'collapsed', 'hidden'];

export interface PanelState {
  leftNavState: LeftNavState;
  leftVisible: boolean; // derived: leftNavState !== 'hidden'
  bottomVisible: boolean;
  rightVisible: boolean;
  cycleLeft: () => void;
  toggleBottom: () => void;
  toggleRight: () => void;
}

const PanelContext = createContext<PanelState | null>(null);

const STORAGE_KEY = 'v3-panel-state';

interface StoredPanelState {
  leftNavState: LeftNavState;
  bottomVisible: boolean;
  rightVisible: boolean;
}

function loadState(): StoredPanelState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { leftNavState: 'expanded', bottomVisible: true, rightVisible: false };
    const parsed = JSON.parse(raw) as Partial<StoredPanelState>;
    return {
      leftNavState: parsed.leftNavState ?? 'expanded',
      bottomVisible: parsed.bottomVisible ?? true,
      rightVisible: parsed.rightVisible ?? false,
    };
  } catch {
    return { leftNavState: 'expanded', bottomVisible: true, rightVisible: false };
  }
}

function saveState(state: StoredPanelState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / privacy mode errors
  }
}

export function PanelProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StoredPanelState>(() => loadState());

  useEffect(() => {
    saveState(state);
  }, [state]);

  const cycleLeft = useCallback(
    () =>
      setState((prev) => {
        const idx = LEFT_NAV_CYCLE.indexOf(prev.leftNavState);
        const next = LEFT_NAV_CYCLE[(idx + 1) % LEFT_NAV_CYCLE.length];
        return { ...prev, leftNavState: next };
      }),
    [],
  );
  const toggleBottom = useCallback(
    () => setState((prev) => ({ ...prev, bottomVisible: !prev.bottomVisible })),
    [],
  );
  const toggleRight = useCallback(
    () => setState((prev) => ({ ...prev, rightVisible: !prev.rightVisible })),
    [],
  );

  const value = useMemo<PanelState>(
    () => ({
      leftNavState: state.leftNavState,
      leftVisible: state.leftNavState !== 'hidden',
      bottomVisible: state.bottomVisible,
      rightVisible: state.rightVisible,
      cycleLeft,
      toggleBottom,
      toggleRight,
    }),
    [state, cycleLeft, toggleBottom, toggleRight],
  );

  return <PanelContext.Provider value={value}>{children}</PanelContext.Provider>;
}

export function usePanels(): PanelState {
  const ctx = useContext(PanelContext);
  if (!ctx) throw new Error('usePanels must be used within a PanelProvider');
  return ctx;
}
